import React, { useEffect, useMemo, useRef, useState } from 'react';

interface SolidColorCardProps {
    backgroundColor?: string;
    text?: string;
    subText?: string;
    textColor?: string;
    subTextColor?: string;
    style?: React.CSSProperties;
}

/**
 * Pure KPI card (no ECharts)
 * Responsive typography based on container size
 */
const SolidColorCard: React.FC<SolidColorCardProps> = ({
    backgroundColor = 'rgba(255,255,255,0.06)',
    text = '',
    subText = '',
    textColor = '#ffffff',
    subTextColor = 'rgba(255,255,255,0.75)',
    style
}) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [size, setSize] = useState({ width: 300, height: 200 });

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const update = () => {
            const rect = el.getBoundingClientRect();
            const w = Math.max(1, rect.width);
            const h = Math.max(1, rect.height);
            setSize({ width: w, height: h });
        };

        update();

        const ro = new ResizeObserver(() => update());
        ro.observe(el);

        window.addEventListener('resize', update);

        return () => {
            ro.disconnect();
            window.removeEventListener('resize', update);
        };
    }, []);

    const { titleFont, valueFont, subFont, padding, gap } = useMemo(() => {
        const minSide = Math.min(size.width, size.height);

        // These ratios are tuned for dashboard cards
        const value = clamp(minSide * 0.28, 18, 72);
        const sub = clamp(minSide * 0.10, 10, 22);
        const title = clamp(minSide * 0.09, 10, 20);

        const pad = clamp(minSide * 0.08, 8, 18);
        const g = clamp(minSide * 0.04, 4, 12);

        return {
            titleFont: title,
            valueFont: value,
            subFont: sub,
            padding: pad,
            gap: g
        };
    }, [size.width, size.height]);

    return (
        <div
            ref={containerRef}
            style={{
                width: '100%',
                height: '100%',
                borderRadius: 10,
                background: backgroundColor,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                padding,
                gap,
                overflow: 'hidden',
                ...style
            }}
        >
            {/* Optional small title line (if you later want to show it) */}
            {/* <div
                style={{
                    fontSize: titleFont,
                    color: subTextColor,
                    opacity: 0.9,
                    lineHeight: 1.1,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '100%'
                }}
                title={subText}
            >
                {subText}
            </div> */}

            <div
                style={{
                    fontSize: valueFont,
                    color: textColor,
                    fontWeight: 700,
                    lineHeight: 1.05,
                    letterSpacing: 0.3,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '100%'
                }}
                title={text}
            >
                {text}
            </div>

            {subText ? (
                <div
                    style={{
                        fontSize: subFont,
                        color: subTextColor,
                        lineHeight: 1.2,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '100%'
                    }}
                    title={subText}
                >
                    {subText}
                </div>
            ) : null}
        </div>
    );
};

function clamp(v: number, min: number, max: number) {
    return Math.max(min, Math.min(max, v));
}

export default SolidColorCard;
